import React, { useEffect } from 'react';
import { Check, Mail, Phone, Calendar as CalendarIcon, MessageSquare, Smartphone } from 'lucide-react';
import { usePageTitle } from '../hooks/usePageTitle';
import { trackPricingView } from '../utils/analytics';
import { APP_NAME } from '../config/branding';

const Pricing: React.FC = () => {
  usePageTitle('Pricing - Tailored Plans for Veterinary Practices');

  useEffect(() => {
    trackPricingView();
  }, []);

  const allFeatures = [
    { icon: CalendarIcon, name: 'Calendar', description: 'Visual scheduling with swim lanes and real-time patient flow tracking for your clinic.' },
    { icon: CalendarIcon, name: 'Online Booking', description: 'Embeddable appointment booking widget for your website with real-time availability.' },
    { icon: Smartphone, name: 'Mobile Forms', description: 'Digital check-in forms that clients complete on their phone before arrival.' },
    { icon: MessageSquare, name: '2-Way Texting', description: 'Send and receive SMS & MMS messages with clients directly from your dashboard.' },
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <div className="pt-20 pb-12 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6 tracking-tight">
            Pricing tailored to your practice
          </h1>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            Every feature included. We work with you to build a plan that fits your practice size and goals.
          </p>
        </div>
      </div>

      {/* Contact Sales Card */}
      <div className="max-w-lg mx-auto px-4 sm:px-6 lg:px-8 pb-16">
        <div className="bg-white rounded-2xl border-2 border-blue-600 p-8 flex flex-col relative shadow-lg">
          <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-blue-600 text-white px-4 py-1 rounded-full text-sm font-medium">
            All Features Included
          </div>

          <div className="mb-6 text-center">
            <h3 className="text-2xl font-bold text-gray-900">Per Location</h3>
            <p className="text-gray-500 mt-1">Everything your practice needs, priced per seat</p>
          </div>

          <div className="mb-6 text-center">
            <p className="text-lg text-gray-600">
              Pricing is negotiated based on your practice size, number of locations, and specific needs.
            </p>
          </div>

          <a
            href="mailto:hi@helppet.ai?subject=Interested%20in%20${encodeURIComponent(APP_NAME)}"
            className="w-full bg-blue-600 text-white py-4 rounded-xl font-semibold hover:bg-blue-700 transition-colors shadow-sm flex items-center justify-center mb-4 text-lg"
          >
            <Mail className="h-5 w-5 mr-2" />
            Contact Sales
          </a>

          <a
            href="tel:+14192831624"
            className="w-full bg-white text-gray-700 py-3 rounded-xl font-semibold border border-gray-300 hover:bg-gray-50 transition-colors flex items-center justify-center mb-6 text-sm"
          >
            <Phone className="h-4 w-4 mr-2" />
            
          </a>

          <ul className="space-y-3 flex-1">
            <li className="flex items-start text-sm">
              <Check className="h-5 w-5 text-blue-600 mr-3 flex-shrink-0" />
              <span className="text-gray-600">Unlimited team members</span>
            </li>
            <li className="flex items-start text-sm">
              <Check className="h-5 w-5 text-blue-600 mr-3 flex-shrink-0" />
              <span className="text-gray-600">Visit Transcriptions</span>
            </li>
            <li className="flex items-start text-sm">
              <Check className="h-5 w-5 text-blue-600 mr-3 flex-shrink-0" />
              <span className="text-gray-600">Medical Records</span>
            </li>
            <li className="flex items-start text-sm">
              <Check className="h-5 w-5 text-blue-600 mr-3 flex-shrink-0" />
              <span className="text-gray-600">Online Booking & Calendar</span>
            </li>
            <li className="flex items-start text-sm">
              <Check className="h-5 w-5 text-blue-600 mr-3 flex-shrink-0" />
              <span className="text-gray-600">2-Way Texting</span>
            </li>
            <li className="flex items-start text-sm">
              <Check className="h-5 w-5 text-blue-600 mr-3 flex-shrink-0" />
              <span className="text-gray-600">AI Front Desk</span>
            </li>
            <li className="flex items-start text-sm">
              <Check className="h-5 w-5 text-blue-600 mr-3 flex-shrink-0" />
              <span className="text-gray-600">Dedicated support partner</span>
            </li>
            <li className="flex items-start text-sm">
              <Check className="h-5 w-5 text-blue-600 mr-3 flex-shrink-0" />
              <span className="text-gray-600">White-glove onboarding</span>
            </li>
          </ul>
        </div>

        <div className="mt-8 text-center">
          <p className="text-gray-600">
            Multiple locations? <a href="mailto:hi@helppet.ai" className="text-blue-600 font-medium hover:underline">Contact us</a> for volume pricing.
          </p>
        </div>
      </div>

      {/* Features Grid */}
      <div className="bg-gray-50 py-20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Everything you need to run your practice</h2>
            <p className="text-lg text-gray-600">Every feature included in your plan</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {allFeatures.map((feature, index) => {
              const Icon = feature.icon;
              const bgColors = ['bg-orange-50', 'bg-blue-50', 'bg-red-50', 'bg-teal-50'];
              const iconColors = ['text-orange-500', 'text-blue-500', 'text-red-500', 'text-teal-500'];
              return (
                <div key={index} className="bg-white rounded-xl p-6">
                  <div className={`w-12 h-12 ${bgColors[index]} rounded-xl flex items-center justify-center mb-4`}>
                    <Icon className={`h-6 w-6 ${iconColors[index]}`} />
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 mb-2">{feature.name}</h3>
                  <p className="text-gray-600 text-sm leading-relaxed">{feature.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Full Feature Table (kept for SEO and marketing value) */}
      <div className="py-20 max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="text-3xl font-bold text-gray-900 mb-4">One platform, everything included</h2>
          <p className="text-lg text-gray-600">From AI scribe to marketing automation. No add-ons, no surprises</p>
        </div>

        <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-200 bg-gray-50">
                <th className="py-4 px-6 text-left text-sm font-medium text-gray-500">Feature</th>
                <th className="py-4 px-6 text-center text-lg font-bold text-blue-600">Included</th>
              </tr>
            </thead>
            <tbody>
              {[
                { category: 'AI Scribe', items: ['Visit Transcriptions', 'Scribe iPhone App', 'AI-Generated SOAP Notes'] },
                { category: 'AI Front Desk', items: ['AI Phone Answering', 'Custom Phone Number', 'Call Transcripts', 'Sentiment Analysis', 'Call History & Analytics', 'Voice Selection & Configuration'] },
                { category: 'Medical Records', items: ['Complete SOAP Documentation', 'Customizable SOAP Templates', 'Vitals & Body Map', 'Orders & Prescriptions', 'Discharge Instructions', 'Attachments & Images'] },
                { category: 'Scheduling & Patient Flow', items: ['Online Booking', 'Calendar with Swim Lanes', 'Patient Flow Kanban Board', 'Waitlist & Walk-ins', 'Google Calendar Sync'] },
                { category: 'Staff Scheduling', items: ['Work Schedule Calendar', 'Recurring Schedules', 'Team Invitations & Roles'] },
                { category: 'Inventory & Purchasing', items: ['Inventory Dashboard', 'Stock Tracking & Adjustments', 'Purchase Orders', 'Vendor Management', 'Low Stock Alerts', 'Inventory Reports'] },
                { category: 'Service Catalog', items: ['Procedures Catalog', 'Treatments Catalog', 'Medications Catalog', 'Labs & Diagnostics Catalog', 'Immunizations Catalog'] },
                { category: 'Consent & Compliance', items: ['Digital Consent Forms', 'E-Signatures', 'SMS Consent Links', 'Audit Logs'] },
                { category: 'Automated Reminders', items: ['Appointment Reminders', 'Vaccine Due Reminders', 'Overdue Vaccine Reminders', 'Due for Visit Reminders', 'Pre-Appointment Form Triggers', 'Configurable Timing & Cadence'] },
                { category: 'Marketing & CRM', items: ['Client CRM', 'Smart Audiences', 'Email Campaigns', 'SMS Campaigns', 'Visual Email Builder', 'Message Templates', 'Open & Read Tracking', 'Click & Link Tracking', 'Delivery Analytics'] },
                { category: 'Digital Forms & Surveys', items: ['Custom Form Builder', 'Drag & Drop Field Types', 'Patient Intake Forms', 'Client Surveys & Feedback', 'Mobile-Friendly Forms', 'Form Submissions Dashboard', 'Auto-Sync to Encounter Intake'] },
                { category: 'Client Communication', items: ['2-Way SMS & MMS (with images)', 'Custom Email Address'] },
                { category: 'Team Chat', items: ['Team Messaging', 'Smart Cards (Pets, Encounters, Appointments)', 'Image & File Attachments', 'In-App Notifications'] },
                { category: 'Website', items: ['Website Hosting', 'Custom Domain'] },
                { category: 'Support & SLA', items: ['Dedicated Support Partner', 'White-Glove Onboarding', '99.9% Uptime SLA Guarantee'] },
                { category: 'Bells & Whistles', items: ['Dark Mode', 'Keyboard Shortcuts'] },
              ].map((section) => (
                <React.Fragment key={section.category}>
                  <tr className="bg-gray-50">
                    <td colSpan={2} className="py-3 px-6 text-xs font-semibold text-gray-500 uppercase tracking-wider">{section.category}</td>
                  </tr>
                  {section.items.map((item, i) => (
                    <tr key={item} className={i < section.items.length - 1 ? 'border-b border-gray-100' : ''}>
                      <td className="py-3 px-6 text-sm text-gray-900">{item}</td>
                      <td className="py-3 px-6 text-center"><Check className="h-5 w-5 text-green-500 mx-auto" /></td>
                    </tr>
                  ))}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>

        {/* Bottom CTA */}
        <div className="mt-12 text-center">
          <a
            href="mailto:hi@helppet.ai"
            className="inline-flex items-center bg-blue-600 text-white py-3 px-8 rounded-xl font-semibold hover:bg-blue-700 transition-colors shadow-sm text-lg"
          >
            <Mail className="h-5 w-5 mr-2" />
            Contact Sales
          </a>
        </div>
      </div>
    </div>
  );
};

export default Pricing;
